/* Kennicott Cart Monitor — Simplified
   - Manages Delivery Date attribute only
   - Validates cart items against product metafields (availableFrom/availableTo)
   - Syncs date selection across all date sliders
   - No external API calls or polling
*/
(() => {
  'use strict';
  if (window.KennicottGlobalCartMonitor) return;

  // ---- Config / Log ---------------------------------------------------------
  const LOG = '[CartMonitor]';
  const log = {
    i: (...a) => console.log(LOG, ...a),
    w: (...a) => console.warn(LOG, ...a),
    e: (...a) => console.error(LOG, ...a),
  };
  const VERBOSE = false;
  const dbg = (...a) => VERBOSE && console.log(LOG, '🐛', ...a);

  // ---- Initialize DateManager -----------------------------------------------
  // Ensure DateManager is available (loaded via date-manager.js)
  if (typeof DateManager === 'undefined') {
    log.e('DateManager not found! Make sure date-manager.js is loaded before this script.');
    return;
  }
  const dateManager = new DateManager();

  // ---- Utility Functions ----------------------------------------------------
  
  // Format date as MM/DD/YYYY (parse ISO string directly to avoid timezone issues)
  const formatDate = (isoDate) => {
    if (!isoDate) return '';
    // Parse ISO date string directly (YYYY-MM-DD) to avoid timezone conversion
    const match = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, yyyy, mm, dd] = match;
      return `${mm}/${dd}/${yyyy}`;
    }
    // Fallback to Date object if not ISO format
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  // Calculate ship date (configurable days before delivery, skipping weekends)
  const calculateShipDate = (deliveryDate, daysBeforeDelivery = 3) => {
    if (!deliveryDate) return null;
    
    // Parse ISO date string directly
    const match = String(deliveryDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    
    const [, yyyy, mm, dd] = match;
    const delivery = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
    
    // Subtract business days (skip weekends)
    let shipDate = new Date(delivery);
    let daysSubtracted = 0;
    
    while (daysSubtracted < daysBeforeDelivery) {
      shipDate.setDate(shipDate.getDate() - 1);
      const dayOfWeek = shipDate.getDay();
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        daysSubtracted++;
      }
    }
    
    // Format as MM/DD/YYYY
    const shipMM = String(shipDate.getMonth() + 1).padStart(2, '0');
    const shipDD = String(shipDate.getDate()).padStart(2, '0');
    const shipYYYY = shipDate.getFullYear();
    return `${shipMM}/${shipDD}/${shipYYYY}`;
  };



  // Read cart from Shopify
  const readCart = async () => {
    try {
      const res = await fetch('/cart.js', { 
        credentials: 'same-origin',
        cache: 'no-store'
      });
      return await res.json();
    } catch (e) {
      log.e('Failed to read cart:', e);
      return { items: [] };
    }
  };

  // Get product metafields from card element
  const getProductMetafields = (variantId, productId = null) => {
    try {
      let card = null;
      
      // Strategy 1: Find by variant ID in data-variants-ids attribute (comma-separated list)
      if (variantId) {
        // Try exact match first (variant ID might be the only one)
        card = document.querySelector(`[data-variants-ids="${variantId}"]`);
        
        // Try substring match for comma-separated lists
        if (!card) {
          card = document.querySelector(`[data-variants-ids*="${variantId}"]`);
        }
        
        // More robust: check all cards with data-variants-ids and see if variantId is in the list
        if (!card) {
          const allCards = document.querySelectorAll('[data-variants-ids]');
          for (const candidateCard of allCards) {
            const variantsIds = candidateCard.getAttribute('data-variants-ids');
            if (variantsIds && variantsIds.split(',').includes(variantId.toString())) {
              card = candidateCard;
              break;
            }
          }
        }
      }
      
      // Strategy 2: Find by product ID as fallback
      if (!card && productId) {
        card = document.querySelector(`[data-product-id="${productId}"]`);
      }
      
      // Strategy 3: Check if we're on a product page - metafields might be in page-level elements
      if (!card) {
        // Try to find product form or product wrapper on product page
        const productForm = document.querySelector('form[action*="/cart/add"]');
        if (productForm) {
          // Look for product data in the form or its parent
          card = productForm.closest('[data-product-id]') || 
                 productForm.querySelector('[data-product-id]') ||
                 productForm.closest('.product') ||
                 document.querySelector('.product[data-product-id]') ||
                 document.querySelector('[data-product-id]');
        }
      }
      
      // Strategy 4: Try to find any element with metafields that might be related
      if (!card && variantId) {
        // Try finding product wrapper or main product element
        const mainProduct = document.querySelector('main [data-product-id], .product-single [data-product-id]');
        if (mainProduct) {
          card = mainProduct;
        }
      }
      
      if (!card) {
        dbg('⚠️ Could not find product card for variant:', variantId, 'productId:', productId);
        // Debug: log all available cards for troubleshooting
        const allCardsWithIds = document.querySelectorAll('[data-variants-ids]');
        dbg('Available cards with variant IDs:', Array.from(allCardsWithIds).map(c => ({
          ids: c.getAttribute('data-variants-ids'),
          productId: c.getAttribute('data-product-id'),
          from: c.getAttribute('data-available-from'),
          to: c.getAttribute('data-available-to')
        })));
        return { availableFrom: null, availableTo: null };
      }
      
      const availableFrom = card.getAttribute('data-available-from')?.trim() || null;
      const availableTo = card.getAttribute('data-available-to')?.trim() || null;
      
      dbg('✅ Found product card. Metafields:', { 
        variantId, 
        productId: card.getAttribute('data-product-id'),
        availableFrom, 
        availableTo,
        cardClasses: card.className
      });
      
      return {
        availableFrom: availableFrom || null,
        availableTo: availableTo || null
      };
    } catch (e) {
      log.w('Failed to get product metafields:', e);
      return { availableFrom: null, availableTo: null };
    }
  };

  // Show notification to user
  const showNotification = ({ title, message, type = 'info', duration = 8000 }) => {
    const colors = {
      error: '#d32f2f',
      warning: '#f57c00',
      info: '#007bff',
      success: '#2e7d32'
    };

    let notification = document.querySelector('.cart-notification');
    if (!notification) {
      notification = document.createElement('div');
      notification.className = 'cart-notification';
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: ${colors[type]};
        color: white;
        padding: 16px 24px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        max-width: 400px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        line-height: 1.4;
      `;
      document.body.appendChild(notification);
    } else {
      notification.style.background = colors[type];
    }
    
    notification.innerHTML = `
      <div style="display: flex; align-items: flex-start; gap: 12px;">
        <div style="flex: 1;">
          ${title ? `<strong>${title}</strong><br>` : ''}
          ${message}
        </div>
        <button onclick="this.parentElement.parentElement.remove()" style="
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          font-size: 18px;
          padding: 0;
          margin-left: 8px;
        ">×</button>
      </div>
    `;
    
    if (duration > 0) {
      setTimeout(() => {
        if (notification && notification.parentElement) {
          notification.remove();
        }
      }, duration);
    }
  };

  // Show modal dialog
  const showModal = ({ title, message, confirmText = 'Continue', cancelText = 'Cancel', onConfirm, onCancel }) => {
    const modal = document.createElement('div');
    modal.className = 'cart-modal-overlay';
    modal.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10001;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;

    modal.innerHTML = `
      <div style="
        background: white;
        border-radius: 12px;
        max-width: 500px;
        width: 90%;
        padding: 24px;
        box-shadow: 0 8px 24px rgba(0,0,0,0.2);
      ">
        <h3 style="margin: 0 0 16px 0; font-size: 20px; font-weight: 600; color: #333;">
          ${title}
        </h3>
        <div style="margin-bottom: 24px; font-size: 14px; line-height: 1.6; color: #666;">
          ${message}
        </div>
        <div style="display: flex; gap: 12px; justify-content: flex-end;">
          <button class="modal-cancel" style="
            padding: 10px 20px;
            border: 1px solid #ddd;
            background: white;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            color: #666;
          ">${cancelText}</button>
          <button class="modal-confirm" style="
            padding: 10px 20px;
            border: none;
            background: #007bff;
            color: white;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
          ">${confirmText}</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const remove = () => {
      if (modal.parentElement) modal.remove();
    };

    modal.querySelector('.modal-confirm').addEventListener('click', () => {
      remove();
      if (onConfirm) onConfirm();
    });

    modal.querySelector('.modal-cancel').addEventListener('click', () => {
      remove();
      if (onCancel) onCancel();
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        remove();
        if (onCancel) onCancel();
      }
    });
  };

  // ---- Date Validation using Product Metafields -----------------------------
  
  // Check if a date is within the availableFrom/availableTo range
  const isDateInRange = (date, availableFrom, availableTo) => {
    if (!date) return false;
    
    const checkDate = new Date(date + 'T00:00:00');
    
    // Check availableFrom: date must not be before availableFrom
    if (availableFrom) {
      const fromDate = new Date(availableFrom + 'T00:00:00');
      if (checkDate < fromDate) {
        return false;
      }
    }
    
    // Check availableTo: date must not be greater than availableTo
    if (availableTo) {
      const toDate = new Date(availableTo + 'T00:00:00');
      if (checkDate > toDate) {
        return false;
      }
    }
    
    return true;
  };

  // Check if an item is a Route protection product (should be excluded from date validation)
  const isRouteProduct = (item) => {
    if (!item) return false;

    // Check vendor
    if (item.vendor && item.vendor.toLowerCase().includes('route')) {
      return true;
    }

    // Check product type
    if (item.product_type && 
        (item.product_type.toLowerCase().includes('route') || 
         item.product_type.toLowerCase().includes('insurance') ||
         item.product_type.toLowerCase().includes('protection'))) {
      return true;
    }

    // Check SKU
    if (item.sku && item.sku.toLowerCase().includes('route')) {
      return true;
    }

    // Check product title
    if (item.product_title && item.product_title.toLowerCase().includes('route')) {
      return true;
    }
    
    if (item.title && item.title.toLowerCase().includes('route')) {
      return true;
    }

    return false;
  };

  // Find the common date range for all cart items
  const findCommonDateRange = (cartItems) => {
    if (!cartItems || cartItems.length === 0) {
      return { hasCommonRange: true, commonFrom: null, commonTo: null };
    }

    let commonFrom = null;
    let commonTo = null;
    const itemRanges = [];

    cartItems.forEach(item => {
      // Skip Route protection products - they don't have date restrictions
      if (isRouteProduct(item)) {
        dbg('⏭️ Skipping Route product:', item.title || item.product_title);
        return;
      }
      
      // Get metafields from line item properties (stored when added to cart)
      // Normalize dates to YYYY-MM-DD regardless of format stored
      const norm = window.DateNormalize ? window.DateNormalize.toISO.bind(window.DateNormalize) : (d) => d ? String(d).slice(0,10) : null;
      let availableFrom = norm(item.properties?.['Available From']) || null;
      let availableTo = norm(item.properties?.['Available To']) || null;
      
      // If metafields not in properties, try to get from product card (fallback)
      if (!availableFrom && !availableTo && item.variant_id) {
        const metafields = getProductMetafields(item.variant_id.toString());
        availableFrom = metafields.availableFrom;
        availableTo = metafields.availableTo;
      }

      // If no date restrictions, this item is always available
      if (!availableFrom && !availableTo) {
        return; // Skip this item - it doesn't restrict the range
      }

      itemRanges.push({
        title: item.title || item.product_title,
        from: availableFrom,
        to: availableTo
      });

      // Calculate intersection of date ranges
      if (availableFrom) {
        const fromDate = new Date(availableFrom + 'T00:00:00');
        if (!commonFrom || fromDate > commonFrom) {
          commonFrom = fromDate; // Latest start date
        }
      }

      if (availableTo) {
        const toDate = new Date(availableTo + 'T00:00:00');
        if (!commonTo || toDate < commonTo) {
          commonTo = toDate; // Earliest end date
        }
      }
    });

    // Check if there's a valid overlap
    const hasCommonRange = !commonFrom || !commonTo || commonFrom <= commonTo;

    dbg('📅 Common date range:', {
      hasCommonRange,
      commonFrom: commonFrom ? commonFrom.toISOString().split('T')[0] : null,
      commonTo: commonTo ? commonTo.toISOString().split('T')[0] : null,
      itemRanges
    });

    return {
      hasCommonRange,
      commonFrom: commonFrom ? commonFrom.toISOString().split('T')[0] : null,
      commonTo: commonTo ? commonTo.toISOString().split('T')[0] : null,
      itemRanges
    };
  };

  // Get list of all valid dates for cart items (for date slider validation)
  const getValidDatesForCart = (cartItems) => {
    const commonRange = findCommonDateRange(cartItems);
    
    if (!commonRange.hasCommonRange || !commonRange.commonFrom || !commonRange.commonTo) {
      // No common range - return empty array
      return {
        validDates: [],
        unavailableItems: cartItems.filter(item => {
          const from = item.properties?.['Available From'];
          const to = item.properties?.['Available To'];
          return from || to;
        }).map(item => ({
          title: item.title || item.product_title,
          availableFrom: item.properties?.['Available From'],
          availableTo: item.properties?.['Available To']
        }))
      };
    }
    
    // Generate all dates in the common range
    const validDates = [];
    const startDate = new Date(commonRange.commonFrom + 'T00:00:00');
    const endDate = new Date(commonRange.commonTo + 'T00:00:00');
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      // Only include business days (Mon-Fri)
      const dayOfWeek = d.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        validDates.push(d.toISOString().split('T')[0]);
      }
    }
    
    return {
      validDates,
      unavailableItems: []
    };
  };

  // Update global validation state for date slider
  const updateGlobalValidation = async () => {
    try {
      const cart = await readCart();
      const items = cart.items || [];
      
      if (items.length === 0) {
        window._cartDateValidation = {
          hasItems: false,
          validDates: [],
          unavailableItems: []
        };
        return;
      }
      
      const validation = getValidDatesForCart(items);
      window._cartDateValidation = {
        hasItems: true,
        validDates: validation.validDates,
        unavailableItems: validation.unavailableItems
      };
      
      dbg('📅 Updated global validation:', window._cartDateValidation);
      
      // Trigger date slider to re-render
      window.dispatchEvent(new CustomEvent('cart:validationUpdated'));
    } catch (e) {
      log.w('Failed to update global validation:', e);
    }
  };

  // Check if cart items are available for a given date using metafields
  const validateCartForDate = (date, cartItems) => {
    dbg('📦 Validating cart items for date:', date);

    const unavailable = [];
    
    cartItems.forEach(item => {
      // Skip Route protection products - they're always available
      if (isRouteProduct(item)) {
        dbg('⏭️ Skipping Route product in validation:', item.title || item.product_title);
        return;
      }
      
      // Get metafields from line item properties (stored when added to cart)
      const availableFrom = item.properties?.['Available From'] || null;
      const availableTo = item.properties?.['Available To'] || null;
      
      // If metafields not in properties, try to get from product card (fallback)
      if (!availableFrom && !availableTo && item.variant_id) {
        const metafields = getProductMetafields(item.variant_id.toString());
        if (metafields.availableFrom || metafields.availableTo) {
          // Use metafields from card, but this won't persist in cart
          dbg('⚠️ Using metafields from card (should be in properties):', item.title);
          if (!isDateInRange(date, metafields.availableFrom, metafields.availableTo)) {
            unavailable.push({
              key: item.key,
              variantId: item.variant_id,
              title: item.title || item.product_title,
              quantity: item.quantity,
              availableFrom: metafields.availableFrom,
              availableTo: metafields.availableTo
            });
          }
          return;
        }
      }
      
      // Validate using properties
      if (!isDateInRange(date, availableFrom, availableTo)) {
        unavailable.push({
          key: item.key,
          variantId: item.variant_id,
          title: item.title || item.product_title,
          quantity: item.quantity,
          availableFrom: availableFrom,
          availableTo: availableTo
        });
      }
    });

    dbg('📦 Validation result:', {
      total: cartItems.length,
      unavailable: unavailable.length,
      items: unavailable.map(i => ({ title: i.title, from: i.availableFrom, to: i.availableTo }))
    });

    return {
      valid: unavailable.length === 0,
      unavailable: unavailable
    };
  };

  // ---- Date Management ------------------------------------------------------
  
  let _isChangingDate = false;

  // Sync existing cart items to new date (improved version)
  const syncExistingItemsToDate = async (newDate) => {
    const cart = await readCart();
    const cartItems = cart.items || [];
    
    if (cartItems.length === 0) {
      dbg('No existing cart items to sync');
      return { synced: [], failed: [], skipped: [], unavailable: [] };
    }

    const formattedNewDate = formatDate(newDate);
    const shipDate = calculateShipDate(newDate);
    
    // Separate items by type
    const routeItems = [];
    const itemsToSync = [];
    const itemsAlreadySynced = [];
    
    cartItems.forEach(item => {
      // Skip Route products - they don't need date properties
      if (isRouteProduct(item)) {
        routeItems.push(item);
        dbg('⏭️ Skipping Route product:', item.title || item.product_title);
        return;
      }
      
      const itemDate = item.properties?.['Delivery Date'];
      if (itemDate && itemDate === formattedNewDate) {
        itemsAlreadySynced.push(item);
      } else {
        itemsToSync.push(item);
      }
    });

    if (itemsToSync.length === 0) {
      dbg('All non-Route cart items already have the new date');
      return { synced: [], failed: [], skipped: routeItems, unavailable: [] };
    }

    dbg('🔄 Items to sync:', itemsToSync.map(i => ({ 
      title: i.title,
      currentDate: i.properties?.['Delivery Date']
    })));

    // Validate items against new date
    const validation = validateCartForDate(newDate, itemsToSync);
    
    if (validation.unavailable.length === itemsToSync.length) {
      // None of the items are available for the new date
      dbg('⚠️ None of the items are available for new date');
      return { 
        synced: [], 
        failed: [], 
        skipped: routeItems, 
        unavailable: validation.unavailable 
      };
    }

    // Determine which items can be synced
    const unavailableKeys = new Set(validation.unavailable.map(item => item.key));
    const itemsAvailableForSync = itemsToSync.filter(item => !unavailableKeys.has(item.key));

    if (itemsAvailableForSync.length === 0) {
      dbg('No items available for syncing to new date');
      return { 
        synced: [], 
        failed: [], 
        skipped: routeItems, 
        unavailable: validation.unavailable 
      };
    }

    dbg('✅ Syncing items to new date:', itemsAvailableForSync.map(i => i.title));

    // Update each available item
    const synced = [];
    const failed = [];

    for (const item of itemsAvailableForSync) {
      try {
        // Shopify returns properties as an array of {name, value} objects
        // We need to convert it to a proper object for updating
        let propertiesObj = {};
        
        if (item.properties) {
          if (Array.isArray(item.properties)) {
            // Convert array format to object format
            item.properties.forEach(prop => {
              if (prop.name && prop.name !== '_') {
                propertiesObj[prop.name] = prop.value;
              }
            });
          } else if (typeof item.properties === 'object') {
            // Already an object
            propertiesObj = { ...item.properties };
          }
        }
        
        // Update with new dates
        propertiesObj['Delivery Date'] = formattedNewDate;
        propertiesObj['Ship Date'] = shipDate;
        
        dbg('📦 Updating item properties:', {
          title: item.title,
          oldProperties: item.properties,
          newProperties: propertiesObj
        });

        const updatePayload = {
          id: item.key,
          quantity: item.quantity,
          properties: propertiesObj
        };

        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(updatePayload)
        });

        if (res.ok) {
          dbg('✅ Synced item:', item.title);
          synced.push(item);
        } else {
          const errorText = await res.text();
          log.w('Failed to sync item:', item.title, res.status, errorText);
          failed.push(item);
        }
      } catch (e) {
        log.e('Error syncing item:', item.title, e);
        failed.push(item);
      }
    }

    return { 
      synced, 
      failed, 
      skipped: routeItems, 
      unavailable: validation.unavailable 
    };
  };

  // Handle date selection change
  const handleDateChange = async (newDate) => {
    if (_isChangingDate) {
      dbg('Date change already in progress, ignoring');
      return;
    }

    const oldDate = dateManager.getSelectedDate();
    if (oldDate === newDate) {
      dbg('Date unchanged, ignoring');
      return;
    }

    dbg('📅 Date change detected:', { oldDate, newDate });

    // Read current cart
    const cart = await readCart();
    const cartItems = cart.items || [];

    // If cart is empty, allow date change immediately
    if (cartItems.length === 0) {
      dbg('Cart is empty, allowing date change');
      dateManager.setSelectedDate(newDate);
      return;
    }

    // Cart has items - need to validate and sync
    _isChangingDate = true;

    showNotification({
      title: 'Updating Cart',
      message: 'Syncing cart items to new delivery date...',
      type: 'info',
      duration: 500
    });

    try {
      // First, try to sync existing items to new date
      const syncResult = await syncExistingItemsToDate(newDate);
      
      // Remove loading notification
      const notification = document.querySelector('.cart-notification');
      if (notification) notification.remove();

      // Check if there are unavailable items
      if (syncResult.unavailable.length > 0) {
        // Some items can't be synced - show modal
        const commonRange = findCommonDateRange(cartItems);
        
        const unavailableList = syncResult.unavailable
          .map(item => {
            const dateRange = item.availableFrom && item.availableTo 
              ? `Available: ${formatDate(item.availableFrom)} - ${formatDate(item.availableTo)}`
              : item.availableFrom 
              ? `Available from: ${formatDate(item.availableFrom)}`
              : item.availableTo
              ? `Available until: ${formatDate(item.availableTo)}`
              : '';
            return `• ${item.title} (${item.quantity}x)${dateRange ? '<br>&nbsp;&nbsp;' + dateRange : ''}`;
          })
          .join('<br>');

        let message = `You're changing your delivery date to <strong>${formatDate(newDate)}</strong>.<br><br>`;
        
        if (commonRange.hasCommonRange && commonRange.commonFrom && commonRange.commonTo) {
          message += `<strong>Note:</strong> All items in your cart can be delivered between <strong>${formatDate(commonRange.commonFrom)}</strong> and <strong>${formatDate(commonRange.commonTo)}</strong>.<br><br>`;
        } else if (!commonRange.hasCommonRange) {
          message += `<strong>Warning:</strong> The items in your cart have no overlapping delivery dates.<br><br>`;
        }
        
        message += `The following items are not available on ${formatDate(newDate)} and will be removed:<br><br>${unavailableList}<br><br>`;
        
        if (syncResult.synced.length > 0) {
          message += `<em>${syncResult.synced.length} item(s) will be updated to the new date.</em><br><br>`;
        }
        
        message += `Do you want to continue?`;

        showModal({
          title: 'Delivery Date Change',
          message: message,
          confirmText: 'Continue',
          cancelText: 'Cancel',
          onConfirm: async () => {
            // Remove unavailable items
            await removeUnavailableItems(syncResult.unavailable);
            dateManager.setSelectedDate(newDate);
            
            // Refresh cart display
            window.dispatchEvent(new CustomEvent('cart:refresh'));
            
            // Update global validation
            await updateGlobalValidation();
            
            let notificationMessage = `Delivery date changed to ${formatDate(newDate)}.`;
            if (syncResult.synced.length > 0) {
              notificationMessage += ` ${syncResult.synced.length} item(s) updated.`;
            }
            if (syncResult.unavailable.length > 0) {
              notificationMessage += ` ${syncResult.unavailable.length} item(s) removed.`;
            }
            
            showNotification({
              title: 'Cart Updated',
              message: notificationMessage,
              type: 'warning',
              duration: 8000
            });
          },
          onCancel: () => {
            dateManager.syncDateSliders(oldDate);
            showNotification({
              title: 'Cancelled',
              message: 'Your delivery date was not changed.',
              type: 'info'
            });
          }
        });
      } else {
        // All items synced successfully
        dateManager.setSelectedDate(newDate);
        
        // Refresh cart display
        window.dispatchEvent(new CustomEvent('cart:refresh'));
        
        // Update global validation
        await updateGlobalValidation();
        
        let message = `Delivery date changed to ${formatDate(newDate)}.`;
        if (syncResult.synced.length > 0) {
          message += ` ${syncResult.synced.length} item(s) updated.`;
        }
        if (syncResult.skipped.length > 0) {
          message += ` (${syncResult.skipped.length} protection item(s) unchanged)`;
        }
        
        showNotification({
          title: 'Date Updated',
          message: message,
          type: 'success',
          duration: 6000
        });
      }
    } catch (error) {
      log.e('Error during date change:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to update cart items. Please try again.',
        type: 'error'
      });
    } finally {
      _isChangingDate = false;
    }
  };

  // Remove items from cart
  const removeUnavailableItems = async (items) => {
    for (const item of items) {
      try {
        dbg('🗑️ Removing item from cart:', item.key);
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: item.key, quantity: 0 })
        });
      } catch (e) {
        log.e('Failed to remove item:', item.key, e);
      }
    }

    // Refresh cart display
    window.dispatchEvent(new CustomEvent('cart:refresh'));
  };

  // ---- Cart Add Interception ------------------------------------------------
  // REMOVED: All add-to-cart interception logic has been removed.
  // Products now add to cart freely without modification.
  // Validation happens at cart view time via CartValidator module.

  // ---- Date Slider Event Listeners ------------------------------------------
  
  // Listen for date selection from any date slider
  window.addEventListener('kennicott:dateSelected', (e) => {
    const newDate = e.detail?.date || dateManager.getSelectedDate();
    if (newDate) {
      handleDateChange(newDate);
    }
  });

  // Listen for clicks on date chips
  document.addEventListener('click', (e) => {
    const chip = e.target.closest('.komet-ds__chip');
    if (chip) {
      const newDate = chip.dataset.iso;
      if (newDate) {
        dbg('📅 Date chip clicked:', newDate);
        handleDateChange(newDate);
      }
    }
  });

  // ---- Cart Add Interception ------------------------------------------------
  
  // Add delivery date, ship date, and availability metafields to cart items

  // Show a notification when a blackout date blocks add-to-cart
  const showBlackoutNotification = (date) => {
    const formatted = date ? (() => {
      const [y, m, d] = date.split('-');
      return `${m}/${d}/${y}`;
    })() : date;

    if (window.NotificationBanner) {
      window.NotificationBanner.show({
        message: `${formatted} is unavailable for delivery. Please select a different date.`,
        type: 'error',
        id: 'blackout-notification'
      });
    }
  };
  const addDatePropertiesToBody = (body, deliveryDate, variantId = null, productId = null) => {
    if (!deliveryDate) return body;
    
    const formattedDate = formatDate(deliveryDate);
    const shipDate = calculateShipDate(deliveryDate);
    
    // Get metafields from DOM if available
    let metafields = { availableFrom: null, availableTo: null };
    if (variantId || productId) {
      metafields = getProductMetafields(variantId, productId);
      dbg('📦 Got metafields from DOM:', metafields);
    }
    
    if (body instanceof FormData) {
      body.set('properties[Delivery Date]', formattedDate);
      if (shipDate) {
        body.set('properties[Ship Date]', shipDate);
      }
      // Add availability metafields if found
      if (metafields.availableFrom) {
        body.set('properties[Available From]', metafields.availableFrom);
      }
      if (metafields.availableTo) {
        body.set('properties[Available To]', metafields.availableTo);
      }
      return body;
    } else if (typeof body === 'string') {
      // Check if it's JSON or URL-encoded
      if (body.trim().startsWith('{')) {
        // JSON string
        try {
          const parsed = JSON.parse(body);
          if (!parsed.properties) parsed.properties = {};
          parsed.properties['Delivery Date'] = formattedDate;
          if (shipDate) {
            parsed.properties['Ship Date'] = shipDate;
          }
          // Add availability metafields if found
          if (metafields.availableFrom) {
            parsed.properties['Available From'] = metafields.availableFrom;
          }
          if (metafields.availableTo) {
            parsed.properties['Available To'] = metafields.availableTo;
          }
          return JSON.stringify(parsed);
        } catch (e) {
          log.w('Failed to parse JSON body:', e);
          return body;
        }
      } else {
        // URL-encoded
        const params = new URLSearchParams(body);
        params.set('properties[Delivery Date]', formattedDate);
        if (shipDate) {
          params.set('properties[Ship Date]', shipDate);
        }
        // Add availability metafields if found
        if (metafields.availableFrom) {
          params.set('properties[Available From]', metafields.availableFrom);
        }
        if (metafields.availableTo) {
          params.set('properties[Available To]', metafields.availableTo);
        }
        return params.toString();
      }
    } else if (body && typeof body === 'object') {
      // Plain object - will be stringified by fetch
      if (!body.properties) body.properties = {};
      body.properties['Delivery Date'] = formattedDate;
      if (shipDate) {
        body.properties['Ship Date'] = shipDate;
      }
      // Add availability metafields if found
      if (metafields.availableFrom) {
        body.properties['Available From'] = metafields.availableFrom;
      }
      if (metafields.availableTo) {
        body.properties['Available To'] = metafields.availableTo;
      }
      // Don't stringify here - let the caller handle it
      return body;
    }
    
    return body;
  };

  // Check if this is a Route product being added (don't add date properties)
  const isRouteProductFromBody = (body) => {
    let variantId = null;
    let productData = {};
    
    if (body instanceof FormData) {
      variantId = body.get('id') || body.get('variant_id');
      // Check for Route-specific form fields
      if (body.get('_route') || body.get('route_insurance') || body.get('_route_insurance')) {
        dbg('🛡️ Detected Route product from form data fields');
        return true;
      }
    } else if (typeof body === 'string') {
      // Check if JSON
      if (body.trim().startsWith('{')) {
        try {
          productData = JSON.parse(body);
          variantId = productData.id || productData.variant_id;
          // Check for Route-specific JSON fields
          if (productData._route || productData.route_insurance || productData._route_insurance) {
            dbg('🛡️ Detected Route product from JSON fields');
            return true;
          }
        } catch (e) {
          // Not JSON, try URL params
          const params = new URLSearchParams(body);
          variantId = params.get('id') || params.get('variant_id');
          if (params.get('_route') || params.get('route_insurance') || params.get('_route_insurance')) {
            dbg('🛡️ Detected Route product from URL params');
            return true;
          }
        }
      } else {
        const params = new URLSearchParams(body);
        variantId = params.get('id') || params.get('variant_id');
        if (params.get('_route') || params.get('route_insurance') || params.get('_route_insurance')) {
          dbg('🛡️ Detected Route product from URL params');
          return true;
        }
      }
    } else if (body && typeof body === 'object') {
      variantId = body.id || body.variant_id;
      // Check for Route-specific object fields
      if (body._route || body.route_insurance || body._route_insurance) {
        dbg('🛡️ Detected Route product from object fields');
        return true;
      }
    }
    
    if (!variantId) {
      // Can't determine - assume it's NOT Route to be safe
      return false;
    }
    
    // Try to find product info from DOM
    const productCard = document.querySelector(`[data-variants-ids*="${variantId}"]`) ||
                       document.querySelector(`[data-variant-id="${variantId}"]`);
    
    if (productCard) {
      const productTitle = productCard.getAttribute('data-product-title') || 
                          productCard.querySelector('.product-title, .card__heading')?.textContent || '';
      const vendor = productCard.getAttribute('data-vendor') || '';
      
      // Check if it's a Route product
      if (productTitle.toLowerCase().includes('route') || 
          vendor.toLowerCase().includes('route')) {
        dbg('🛡️ Detected Route product from DOM');
        return true;
      }
    }
    
    return false;
  };

  // Extract variant ID from request body
  const extractVariantId = (body) => {
    if (body instanceof FormData) {
      return body.get('id') || body.get('variant_id');
    } else if (typeof body === 'string') {
      if (body.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(body);
          return parsed.id || parsed.variant_id;
        } catch (e) {
          const params = new URLSearchParams(body);
          return params.get('id') || params.get('variant_id');
        }
      } else {
        const params = new URLSearchParams(body);
        return params.get('id') || params.get('variant_id');
      }
    } else if (body && typeof body === 'object') {
      return body.id || body.variant_id;
    }
    return null;
  };

  // Intercept add-to-cart requests
  const interceptCartAdd = () => {
    const processingForms = new WeakSet();
    const processingFetches = new WeakMap();
    
    // Wrap fetch for /cart/add.js
    const originalFetch = window.fetch;
    window.fetch = async function(input, init = {}) {
      const url = typeof input === 'string' ? input : (input?.url || '');
      const method = (init?.method || 'GET').toUpperCase();
      
      if (/\/cart\/add(\.js)?$/.test(url) && method === 'POST') {
        // Prevent double processing
        if (processingFetches.has(init)) {
          return originalFetch.apply(this, arguments);
        }
        
        // Check if from already processed form
        if (init.body instanceof FormData && init.body.get('_cart_monitor_processed') === 'true') {
          return originalFetch.apply(this, arguments);
        }
        
        dbg('🛒 Intercepting add-to-cart fetch');
        processingFetches.set(init, true);
        
        // Check if this is a Route product
        const isRoute = isRouteProductFromBody(init.body);
        
        if (!isRoute) {
          // Block if selected date is a blackout date
          const blackoutDates = window.blackoutDates || [];
          const selectedDate = dateManager.getSelectedDate();
          if (selectedDate && blackoutDates.includes(selectedDate)) {
            dbg('Blocked add-to-cart fetch: blackout date', selectedDate);
            showBlackoutNotification(selectedDate);
            setTimeout(() => processingFetches.delete(init), 1000);
            return Promise.reject(new Error('Blackout date: ' + selectedDate));
          }

          // Get delivery date
          const deliveryDate = dateManager.getSelectedDate();
          
          if (deliveryDate) {
            dbg('📅 Adding delivery date to cart item:', formatDate(deliveryDate));
            
            // Sync existing items first
            try {
              const syncResult = await syncExistingItemsToDate(deliveryDate);
              
              if (syncResult.synced.length > 0) {
                dbg('✅ Synced existing items:', syncResult.synced.length);
              }
              
              if (syncResult.unavailable.length > 0) {
                dbg('⚠️ Some items unavailable, will be handled by modal');
              }
            } catch (e) {
              log.w('Failed to sync existing items:', e);
            }
            
            // Extract variant ID to get metafields from DOM
            const variantId = extractVariantId(init.body);
            
            // Add date properties to new item (including metafields from DOM)
            try {
              const modifiedBody = addDatePropertiesToBody(init.body, deliveryDate, variantId);
              if (modifiedBody !== init.body) {
                init.body = modifiedBody;
                // Only set Content-Type if we converted to JSON string
                if (typeof modifiedBody === 'string' && modifiedBody.startsWith('{')) {
                  if (!init.headers) init.headers = {};
                  init.headers['Content-Type'] = 'application/json';
                }
              }
            } catch (e) {
              log.e('Error adding date properties:', e);
              // Continue with original body if modification fails
            }
          }
        } else {
          dbg('⏭️ Skipping date properties for Route product');
        }
        
        setTimeout(() => processingFetches.delete(init), 1000);
      }
      
      return originalFetch.apply(this, arguments);
    };

    // Intercept form submissions
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (form.action && /\/cart\/add/.test(form.action)) {
        // Let product-form custom element handle it
        if (form.closest('product-form')) {
          return;
        }
        
        if (processingForms.has(form)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        
        dbg('🛒 Intercepting form add-to-cart');
        processingForms.add(form);
        e.preventDefault();
        e.stopPropagation();
        
        // Check if Route product
        const variantInput = form.querySelector('input[name="id"], input[name="variant_id"]');
        const variantId = variantInput?.value;
        const isRoute = isRouteProductFromBody(new FormData(form));
        
        // Build FormData
        const formData = new FormData(form);
        
        if (!isRoute) {
          // Block if selected date is a blackout date
          const blackoutDates = window.blackoutDates || [];
          const selectedDate = dateManager.getSelectedDate();
          if (selectedDate && blackoutDates.includes(selectedDate)) {
            dbg('🚫 Blocked add-to-cart form: blackout date', selectedDate);
            showBlackoutNotification(selectedDate);
            processingForms.delete(form);
            return;
          }

          // Get delivery date
          const deliveryDate = dateManager.getSelectedDate();
          
          if (deliveryDate) {
            // Sync existing items first
            try {
              const syncResult = await syncExistingItemsToDate(deliveryDate);
              
              if (syncResult.synced.length > 0) {
                dbg('✅ Synced existing items:', syncResult.synced.length);
              }
            } catch (e) {
              log.w('Failed to sync existing items:', e);
            }
            
            // Add date properties (including metafields from DOM)
            addDatePropertiesToBody(formData, deliveryDate, variantId);
          }
        }
        
        // Mark as processed
        formData.append('_cart_monitor_processed', 'true');
        
        // Submit
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });
          
          if (response.ok) {
            const result = await response.json();
            
            if (result.status) {
              // Error
              processingForms.delete(form);
              window.dispatchEvent(new CustomEvent('cart:error', {
                detail: { errors: result.errors || result.description }
              }));
              return;
            }
            
            // Success
            window.dispatchEvent(new CustomEvent('cart:refresh'));
            window.dispatchEvent(new CustomEvent('cart:updated', {
              detail: { cartData: result }
            }));
            
            // Update global validation
            await updateGlobalValidation();
            
            processingForms.delete(form);
          } else {
            processingForms.delete(form);
            log.e('Failed to add to cart:', response.status);
          }
        } catch (err) {
          processingForms.delete(form);
          log.e('Error adding to cart:', err);
        }
      }
    }, true);
  };

  // ---- Initialization -------------------------------------------------------
  
  const init = () => {
    dbg('🚀 Initializing Simplified Cart Monitor');
    
    // Intercept add-to-cart to add delivery date properties
    interceptCartAdd();

    // Get and set initial date using DateManager
    const initialDate = dateManager.getSelectedDate();
    if (initialDate) {
      dateManager.syncDateSliders(initialDate);
      dbg('📅 Initial date set:', initialDate);
    }
    
    // Update global validation state
    updateGlobalValidation();
    
    // Listen for cart updates to refresh validation
    window.addEventListener('cart:refresh', () => {
      setTimeout(updateGlobalValidation, 100);
    });
    
    // Listen for cart changes from Shopify
    document.addEventListener('cart:updated', () => {
      setTimeout(updateGlobalValidation, 100);
    });
  };

  // Mark as initialized
  window.KennicottGlobalCartMonitor = { 
    version: '3.0-improved',
    calculateShipDate,
    formatDate,
    isRouteProduct
  };
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
